/* eslint-disable no-confusing-arrow */
import handleActions from 'es6/helpers/handle-actions';
import EntityStore from 'es6/redux/modules/entity-store';

export const ENTITY_PUT = 'entity/PUT';
export const ENTITY_PUT_MANY = 'entity/PUT_MANY';
export const ENTITY_DELETE = 'entity/DELETE';
export const ENTITY_START_LOADING = 'entity/START_LOADING';
export const ENTITY_STOP_LOADING = 'entity/STOP_LOADING';
export const ENTITY_SET_ERROR = 'entity/SET_ERROR';
export const ENTITY_SET_ERRORS = 'entity/SET_ERRORS';
export const ENTITY_CLEAR_ERRORS = 'entity/CLEAR_ERRORS';

/**
  Returns a customised sub-store reducer.

  It will react to actions that have the property `name` set. For
  example, if the store was created this way:

  ```
  var itemsEntity = entity('item', Item);
  ```

  It returns an object of `{ reducer, actions }`, where `actions` are
  action creators:

  - `put(id, data)` to put a single item, its loading flag will be cleared
    (`ENTITY_PUT`)
  - `putMany([[id, value], [id, value], ...])` to put many pairs of `[id, value]`
    their loading flags will be cleared (`ENTITY_PUT_MANY`)
  - `delete(...ids)` to delete one or more `ids` (`ENTITY_DELETE`)
  - `startLoading(...ids)` to mark multiple `ids` as loading
    (`ENTITY_START_LOADING`)
  - `stopLoading(...ids)` to clear loading flag for `ids`
    (`ENTITY_STOP_LOADING`)
  - `setError(id, error)` to set error information about an item,
    clears its loading flag (`ENTITY_SET_ERROR`)
  - `setErrors([[id, value], [id, value] ...])` to set errors for many
    items, their loading flags are cleared (`ENTITY_SET_ERRORS`)
  - `clearErrors(...ids)` to clear error info on `ids`,
    (`ENTITY_CLEAR_ERRORS`)

  @param {string} name - Name for the sub-store. All operations are
  filtered for having this property.

  @param {constructor} ctor - a constructor that will be called
  on all new data coming in, optional
*/
export default function entity(name, ctor) {
  if (!name || typeof name !== 'string') {
    throw new TypeError('Name needs to be a string');
  }

  const defaultState = new EntityStore({ ctor });

  const reducer = handleActions(defaultState, {
    [ENTITY_PUT]:           (state, { id, value }) => state.setEntity(id, value),
    [ENTITY_PUT_MANY]:      (state, { pairs }) => state.setEntities(pairs),
    [ENTITY_DELETE]:        (state, { ids }) => state.deleteEntities(ids),
    [ENTITY_START_LOADING]: (state, { ids }) => state.startLoading(ids),
    [ENTITY_STOP_LOADING]:  (state, { ids }) => state.stopLoading(ids),
    [ENTITY_SET_ERROR]:     (state, { id, error }) => state.setError(id, error),
    [ENTITY_SET_ERRORS]:    (state, { pairs }) => state.setErrors(pairs),
    [ENTITY_CLEAR_ERRORS]:  (state, { ids }) => state.clearErrors(ids),
  });

  const actions = {
    put:          (id, value) => ({ type: ENTITY_PUT, name, id, value }),
    putMany:      (pairs) => ({ type: ENTITY_PUT_MANY, name, pairs }),
    delete:       (...ids) => ({ type: ENTITY_DELETE, name, ids }),
    startLoading: (...ids) => ({ type: ENTITY_START_LOADING, name, ids }),
    stopLoading:  (...ids) => ({ type: ENTITY_STOP_LOADING, name, ids }),
    setError:     (id, error) => ({ type: ENTITY_SET_ERROR, name, id, error }),
    setErrors:    (pairs) => ({ type: ENTITY_SET_ERRORS, name, pairs }),
    clearErrors:  (...ids) => ({ type: ENTITY_CLEAR_ERRORS, name, ids }),
  };

  return { reducer: byName(reducer), actions };

  // Filters the action by name
  function byName(fn) {
    return ((state = defaultState, action = {}) =>
      action.name === name ? fn(state, action) : state);
  }
}
